import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { Icon } from '../../ui/Icon';
import type { ProductDraft } from '../lib/draft.js';
import { priceLabel } from '../lib/list.js';
import { addPackPart, listName, packCandidates, packPreview, partPriceLabel, type MartAdminProduct } from '../lib/mart.js';
import { catalogKeys } from '../keys';
import { Field, ListError, invalid, type Errors, type SetField, type Update } from './fields';

/** Mart: barcode, shelf, the variant label (and which product it's a variant of), and the value pack. */
export function MartSection({
  d,
  errors,
  set,
  update,
  shopId,
  selfId,
  hasVariants,
  editPathFor,
}: {
  d: ProductDraft;
  errors: Errors;
  set: SetField;
  update: Update;
  shopId: string;
  selfId: string | null;
  hasVariants: boolean;
  editPathFor(id: string): string;
}) {
  const isVariant = !!d.variantOf;
  const base = useQuery({
    queryKey: catalogKeys.product(shopId, d.variantOf),
    queryFn: () => api.adminProduct(shopId, d.variantOf),
    enabled: isVariant && !!shopId,
    staleTime: 60_000,
  });
  return (
    <section className="card panel" aria-labelledby="sec-mart">
      <h2 id="sec-mart">Grocery</h2>
      {isVariant ? (
        <div className="note row wrap">
          <Icon name="list" size="sm" />
          <span className="grow t-caption">
            A variant of{' '}
            <Link to={editPathFor(d.variantOf)}>{base.data ? listName(base.data as MartAdminProduct) : 'its base product'}</Link>. It has its own price, stock, barcode and photo; customers pick it on the base product.
          </span>
        </div>
      ) : null}
      <div className="cat-grid2">
        <Field name="barcode" label="Barcode (optional)" error={errors.barcode} help="The number under the barcode lines.">
          <input {...invalid('barcode', errors.barcode)} className="input t-mono" inputMode="numeric" maxLength={32} autoComplete="off" value={d.barcode} onChange={(e) => set('barcode', e.target.value)} />
        </Field>
        <Field name="shelf" label="Shelf (optional)" error={errors.shelf} help="Shown on the pick list, like A3.">
          <input {...invalid('shelf', errors.shelf)} className="input" maxLength={20} value={d.shelf} onChange={(e) => set('shelf', e.target.value)} />
        </Field>
      </div>
      {isVariant || hasVariants || d.variantLabel ? (
        <Field
          name="variantLabel"
          label={isVariant ? 'Variant label' : 'Label for this size (optional)'}
          error={errors.variantLabel}
          help={isVariant ? 'What tells it apart, like 1 L or Sweet. Up to 30 characters.' : 'Shown on the size chips next to the other variants, like 290 ml.'}
        >
          <input {...invalid('variantLabel', errors.variantLabel)} className="input" maxLength={30} autoComplete="off" value={d.variantLabel} onChange={(e) => set('variantLabel', e.target.value)} />
        </Field>
      ) : null}
      {isVariant ? null : <PackBuilder d={d} errors={errors} update={update} shopId={shopId} selfId={selfId} />}
    </section>
  );
}

function PackBuilder({ d, errors, update, shopId, selfId }: { d: ProductDraft; errors: Errors; update: Update; shopId: string; selfId: string | null }) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setTerm(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);
  const picker = useQuery({
    queryKey: catalogKeys.picker(shopId, `mart:${term}`),
    queryFn: () => api.adminProducts(shopId, { q: term || undefined, line: 'mart', status: 'active' }),
    enabled: !!shopId,
    staleTime: 30_000,
  });
  const candidates = packCandidates((picker.data?.items ?? []) as MartAdminProduct[], d.pack, selfId).slice(0, 8);
  const preview = packPreview(d.price, d.pack);

  return (
    <fieldset className="cat-fieldset">
      <legend className="t-label">Value pack</legend>
      <p className="t-caption muted">Bundle this shop’s Grocery items, like 3 noodles + 1 egg. Its stock comes from those items. Leave empty for a single item.</p>
      {d.pack.length ? (
        <ul className="stack s8">
          {d.pack.map((c) => (
            <li key={c.productId} className="cat-combo">
              <span className="grow stack s4">
                <span className="t-label">{c.name}</span>
                <span className="t-caption muted">{partPriceLabel(c)}</span>
              </span>
              <label className="sr-only" htmlFor={`pack-${c.productId}`}>
                Quantity of {c.name}
              </label>
              <div className="input-affix cat-num">
                <span aria-hidden="true">×</span>
                <input
                  id={`pack-${c.productId}`}
                  inputMode="numeric"
                  value={c.qty}
                  onChange={(e) => update((x) => ({ ...x, pack: x.pack.map((y) => (y.productId === c.productId ? { ...y, qty: e.target.value } : y)) }), 'pack')}
                />
              </div>
              <button type="button" className="btn btn-quiet icon-btn" aria-label={`Remove ${c.name} from the pack`} onClick={() => update((x) => ({ ...x, pack: x.pack.filter((y) => y.productId !== c.productId) }), 'pack')}>
                <Icon name="close" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <ListError name="pack" error={errors.pack} />
      {preview ? (
        <div className="note" aria-live="polite">
          <span className="t-label">{preview.text}</span>
        </div>
      ) : d.pack.length ? (
        <span className="t-caption muted">Set the pack price above to see what customers save.</span>
      ) : null}
      <div className="stack s8">
        <label className="search cat-picker">
          <span className="sr-only">Find a Grocery product to add to the pack</span>
          <Icon name="search" />
          <input type="search" placeholder="Add an item: search this shop’s Grocery products" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {picker.isError ? (
          <span className="t-caption muted">Couldn’t load products. Try again in a moment.</span>
        ) : candidates.length ? (
          <ul className="cat-picks" aria-label="Products to add">
            {candidates.map((p) => (
              <li key={p.id}>
                <button type="button" className="btn btn-quiet cat-pick" onClick={() => update((x) => ({ ...x, pack: addPackPart(x.pack, p) }), 'pack')}>
                  <Icon name="plus" size="sm" />
                  <span className="grow">{listName(p)}</span>
                  <span className="t-caption muted">{priceLabel(p)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : picker.data ? (
          <span className="t-caption muted">{term ? 'No matching Grocery products.' : 'No other active Grocery products to add yet.'}</span>
        ) : null}
      </div>
    </fieldset>
  );
}
